import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import Button from "@scripts/react/components/Button";
import { RAPID_EXAMPLE_VIDEOS } from "@scripts/utils/rapidUtils";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useMobile } from "@/hooks/useMobile";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { useCarouselNavigation } from "@/hooks/useCarouselNavigation";
import { FloatingNavigationControls } from "@/components/shared/FloatingNavigationControls";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { fileFromUrl } from "@/utils/fileFromUrl";
import { MediaItem } from "@/types/media";

const createStaggerAnimation = (delay: number) => ({
    initial: { opacity: 0, y: -20, filter: "blur(10px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    exit: { opacity: 0, y: -20, filter: "blur(10px)" },
    transition: { duration: 0.3, ease: "easeOut", delay }
});

interface ExampleVideosProps {
    /** Whether the video trimmer is active (pauses carousel animation) */
    isVideoTrimActive?: boolean;
    /** Callback to add media items when user selects "Use this data" */
    onAddMediaItems?: (files: File[]) => Promise<MediaItem[]>;
    /** Callback to set the text prompt */
    onSetTextPrompt?: (prompt: string) => void;
}

/**
 * ExampleVideos component displays a scrolling carousel of example videos
 * with lazy loading and network-aware visibility controls.
 */
export const ExampleVideos = memo(
    ({ isVideoTrimActive = false, onAddMediaItems, onSetTextPrompt }: ExampleVideosProps) => {
        const { track } = useAnalytics();
        const isMobile = useMobile();
        const [showExamples, setShowExamples] = useState(!isMobile);
        const [selectedVideo, setSelectedVideo] = useState<(typeof RAPID_EXAMPLE_VIDEOS)[0] | null>(
            null
        );
        const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

        // Avoid loading examples on mobile or constrained networks
        useEffect(() => {
            const isConstrainedNetwork = (() => {
                try {
                    const connection =
                        (navigator as any)?.connection ||
                        (navigator as any)?.mozConnection ||
                        (navigator as any)?.webkitConnection;
                    return (
                        connection?.saveData ||
                        ["slow-2g", "2g", "3g"].includes(connection?.effectiveType)
                    );
                } catch {
                    return false; // Assume good connection if API unavailable
                }
            })();

            setShowExamples(!isMobile && !isConstrainedNetwork);
        }, [isMobile]);

        // Repeat videos to create a seamless loop
        const videosForCarousel = useMemo(() => {
            if (!showExamples) return [];
            const videos = RAPID_EXAMPLE_VIDEOS;

            // Repeat enough times to overflow wide screens
            const videosPerScreen = Math.ceil(window.innerWidth / 224);
            // 224 = gap-6 + ~200px video width

            const repeatCount = Math.max(2, Math.ceil(videosPerScreen / videos.length));

            const repeated: typeof videos = [] as any;
            for (let i = 0; i < repeatCount; i++) {
                repeated.push(...videos);
            }
            return repeated;
        }, [showExamples]);

        const handleVideoClick = useCallback(
            (video: (typeof RAPID_EXAMPLE_VIDEOS)[0]) => {
                setSelectedVideo(video);
                const videoIndex = RAPID_EXAMPLE_VIDEOS.findIndex((v) => v.title === video.title);
                setCurrentVideoIndex(videoIndex >= 0 ? videoIndex : 0);
                track({
                    event: AnalyticsFlowEvent.EXAMPLE_VIDEO_CLICKED,
                    data: { videoTitle: video.title }
                });
            },
            [track]
        );

        return (
            <>
                <AnimatePresence mode="wait">
                    {showExamples ? (
                        <motion.div
                            key="example-videos"
                            {...createStaggerAnimation(0.2)}
                            className="relative isolate w-full overflow-hidden py-4"
                        >
                            <div
                                className={twClassNames(
                                    !isVideoTrimActive && "animate-scroll-left",
                                    "flex flex-shrink-0 gap-6"
                                )}
                            >
                                {videosForCarousel.map((video, id) => (
                                    <ExampleVideo
                                        key={id}
                                        src={video.srcSmall}
                                        poster={video.posterSmall}
                                        title={video.title}
                                        onClick={() => handleVideoClick(video)}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="show-examples-button"
                            {...createStaggerAnimation(0.2)}
                            className="m-4 flex h-24 flex-col items-center justify-end gap-2"
                        >
                            {/* @ts-expect-error - Button component type issue */}
                            <Button
                                type="secondary"
                                size="xsmall"
                                onClick={() => setShowExamples(true)}
                            >
                                Show Examples
                            </Button>
                            <p className="text-xs text-tertiary">Hidden to save data</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Example Video Modal */}
                <VideoModal
                    isOpen={!!selectedVideo}
                    onClose={() => setSelectedVideo(null)}
                    currentVideoIndex={currentVideoIndex}
                    setCurrentVideoIndex={setCurrentVideoIndex}
                    onAddMediaItems={onAddMediaItems}
                    onSetTextPrompt={onSetTextPrompt}
                />
            </>
        );
    }
);

ExampleVideos.displayName = "ExampleVideos";

const ExampleVideo = ({
    src,
    poster,
    title,
    onClick
}: {
    src: string;
    poster: string;
    title: string;
    onClick: () => void;
}) => {
    const [shouldLoad, setShouldLoad] = useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const videoRef = React.useRef<HTMLVideoElement | null>(null);

    // Lazily load video source when visible, with delay for everything else to load first
    useEffect(() => {
        if (shouldLoad) return;

        // Wait for page load + additional delay to prioritize main content
        const pageLoadDelay = document.readyState === "complete" ? 500 : 2000;

        const timeoutId = setTimeout(() => {
            const element = containerRef.current;
            if (!element) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (entry && entry.isIntersecting) {
                        setShouldLoad(true);
                        observer.disconnect();
                    }
                },
                { root: null, rootMargin: "300px", threshold: 0 }
            );

            observer.observe(element);
            return () => observer.disconnect();
        }, pageLoadDelay);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [shouldLoad]);

    return (
        <div
            ref={containerRef}
            className="aspect-video relative h-24 flex-shrink-0 transform cursor-pointer rounded-lg border border-gray-200 p-0.5 shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:shadow-xl sm:h-28"
            key={title}
            onClick={onClick}
        >
            <video
                ref={videoRef}
                src={shouldLoad ? src : undefined}
                poster={poster}
                autoPlay={shouldLoad}
                muted
                loop
                playsInline
                preload="none"
                className="h-full w-full rounded-md object-cover"
                title={title}
            />
        </div>
    );
};

const VideoModal = ({
    isOpen,
    onClose,
    currentVideoIndex,
    setCurrentVideoIndex,
    onAddMediaItems,
    onSetTextPrompt
}: {
    isOpen: boolean;
    onClose: () => void;
    currentVideoIndex: number;
    setCurrentVideoIndex: (value: React.SetStateAction<number>) => void;
    onAddMediaItems?: (files: File[]) => Promise<MediaItem[]>;
    onSetTextPrompt?: (prompt: string) => void;
}) => {
    const { track } = useAnalytics();
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    const handleNavigateToVideo = useCallback(
        (_video: any, index: number) => {
            setIsLoading(true);
            setCurrentVideoIndex(index);
        },
        [setCurrentVideoIndex]
    );

    const { handlePrevious: handlePreviousVideo, handleNext: handleNextVideo } =
        useCarouselNavigation({
            items: RAPID_EXAMPLE_VIDEOS,
            currentIndex: currentVideoIndex,
            onNavigate: handleNavigateToVideo
        });

    const currentVideo = RAPID_EXAMPLE_VIDEOS[currentVideoIndex];

    // Keyboard shortcuts for video navigation
    useKeyboardShortcut({ key: "ArrowLeft", handler: handlePreviousVideo, disabled: !isOpen });
    useKeyboardShortcut({ key: "ArrowRight", handler: handleNextVideo, disabled: !isOpen });

    const handleUseThisData = useCallback(async () => {
        if (!onAddMediaItems || isUploading) return;

        try {
            setIsUploading(true);

            // Fetch the video from URL and create a File object
            const file = await fileFromUrl({
                url: currentVideo.originalSrc,
                name: currentVideo.title
            });

            // Add the file to media items
            await onAddMediaItems([file]);

            // Set the text prompt if available
            if (onSetTextPrompt && currentVideo.prompt) {
                onSetTextPrompt(currentVideo.prompt);
            }

            track({
                event: AnalyticsFlowEvent.EXAMPLE_VIDEO_USED,
                data: { videoTitle: currentVideo.title, prompt: currentVideo.prompt }
            });

            // Close modal after successful upload
            onClose();
        } catch (error) {
            console.error("Failed to upload example video:", error);
            // TODO: Show error toast to user
        } finally {
            setIsUploading(false);
        }
    }, [currentVideo, onAddMediaItems, onSetTextPrompt, isUploading, track, onClose]);

    return (
        // @ts-expect-error - ModalWithTitle component type issue
        <ModalWithTitle
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onAddMediaItems ? handleUseThisData : undefined}
            submitButtonText={
                isUploading ? (
                    "Loading..."
                ) : (
                    <>
                        <i className="far fa-arrow-right" /> Try With This Video
                    </>
                )
            }
            submitButtonProps={{
                loading: isUploading,
                loadingText: "Loading...",
                disabled: isUploading
            }}
            title="Example Video"
            icon="far fa-video"
            size="max"
            scrollInsideModal={true}
            panelClassName="h-full"
            overlayClassName="backdrop-blur-sm"
            className="visualArea relative bg-gray-50"
            showFooter={!!onAddMediaItems}
            allowClickPropagation
            allowMouseUpPropagation
        >
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className="fileCarouselWindow relative flex flex-1 flex-col overflow-hidden"
            >
                <FloatingNavigationControls
                    onPrevious={handlePreviousVideo}
                    onNext={handleNextVideo}
                />

                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
                        <i className="far fa-spinner fa-spin text-lg text-tertiary" />
                    </div>
                )}

                <div className="filePreview relative flex-1 flex-col items-center">
                    <video
                        src={currentVideo.srcLarge}
                        poster={currentVideo.poster}
                        controls
                        autoPlay
                        muted
                        loop
                        className={twClassNames(
                            "absolute left-0 top-0 h-full w-full object-contain transition-all duration-300",
                            isLoading ? "opacity-0" : "opacity-100"
                        )}
                        onLoadedData={() => setIsLoading(false)}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                </div>
            </motion.div>
        </ModalWithTitle>
    );
};
